/**
 * pages/admin/healthCheck/runners.ts
 *
 * Individual async runner functions for each HealthCheck.
 * Each function takes the current user and an updateCheck callback,
 * runs one Supabase query/RPC, then calls updateCheck with the result.
 *
 * Updated: 2026-05-11 — added security audit runners (secdef grants,
 *   search_path, storage policies) following security hardening migration.
 * Fixed: 2026-05-11 — replaced String(err) with formatError() helper to
 *   produce readable error messages from PostgrestError objects instead of
 *   "[object Object]".
 * Fixed: 2026-05-11 — removed unused count option in runFormTemplatesOwnCheck.
 * Updated: 2026-05-12 — added runSecDefJustificationCheck, runSecDefAuthenticatedGrantsCheck,
 *   and runRlsTablesWithoutPoliciesCheck.
 * Updated: 2026-05-12 — runSecDefJustificationCheck now distinguishes MISSING entries
 *   (stale allowlist pointing to a non-existent function) from EXISTS entries (missing comment).
 */

import { supabase } from '../../../lib/supabase';
import { captureError } from '../../../utils/errorReporting';
import type { HealthCheck, FkOffender, SecDefOffender, SearchPathOffender, StoragePolicyOffender, MissingJustificationOffender, SecDefAuthOffender, RlsNoPolicyOffender } from './types';

type UpdateCheck = (id: string, patch: Partial<HealthCheck>) => void;
type User = { id: string };

function formatError(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (err !== null && typeof err === 'object') {
    const e = err as Record<string, unknown>;
    const parts: string[] = [];
    if (typeof e.message === 'string') parts.push(e.message);
    if (typeof e.code === 'string') parts.push(`[${e.code}]`);
    if (typeof e.details === 'string') parts.push(e.details);
    if (typeof e.hint === 'string') parts.push(`Hint: ${e.hint}`);
    if (parts.length > 0) return parts.join(' — ');
    return JSON.stringify(err);
  }
  return String(err);
}

export async function runAuthCheck(user: User, updateCheck: UpdateCheck) {
  updateCheck('auth', { status: 'running' });
  try {
    const { data, error } = await supabase
      .from('users')
      .select('id, email, is_admin')
      .eq('id', user.id)
      .maybeSingle();
    if (error) throw error;
    if (!data) {
      updateCheck('auth', { status: 'error', detail: 'Aucun profil trouvé pour cet utilisateur.' });
    } else {
      updateCheck('auth', { status: 'ok', detail: `Profil lu : ${data.email} — admin: ${data.is_admin ? 'oui' : 'non'}` });
    }
  } catch (err) {
    captureError(err, { check: 'auth', context: 'AdminHealthCheck' });
    updateCheck('auth', { status: 'error', detail: formatError(err) });
  }
}

export async function runFormTemplatesOwnCheck(user: User, updateCheck: UpdateCheck) {
  updateCheck('form_templates_own', { status: 'running' });
  try {
    const { error } = await supabase
      .from('form_templates')
      .select('id')
      .eq('user_id', user.id)
      .limit(1);
    if (error) throw error;
    updateCheck('form_templates_own', { status: 'ok', detail: 'Requête réussie. Formulaires propres accessibles.' });
  } catch (err) {
    captureError(err, { check: 'form_templates_own', context: 'AdminHealthCheck' });
    updateCheck('form_templates_own', { status: 'error', detail: formatError(err) });
  }
}

export async function runFormTemplatesAdminCheck(_user: User, updateCheck: UpdateCheck) {
  updateCheck('form_templates_admin', { status: 'running' });
  try {
    const { data, error } = await supabase
      .from('form_templates')
      .select('id, name, created_by')
      .not('created_by', 'is', null)
      .limit(5);
    if (error) throw error;
    const count = data?.length ?? 0;
    if (count === 0) {
      updateCheck('form_templates_admin', {
        status: 'warning',
        detail: "Aucun formulaire admin trouvé (created_by NOT NULL). Normal si aucun formulaire admin n'existe encore.",
      });
    } else {
      updateCheck('form_templates_admin', {
        status: 'ok',
        detail: `${count} formulaire(s) admin lisible(s). RLS correcte.`,
      });
    }
  } catch (err) {
    captureError(err, { check: 'form_templates_admin', context: 'AdminHealthCheck' });
    updateCheck('form_templates_admin', { status: 'error', detail: formatError(err) });
  }
}

export async function runAssignmentsCheck(user: User, updateCheck: UpdateCheck) {
  updateCheck('assignments', { status: 'running' });
  try {
    const { error } = await supabase
      .from('form_template_assignments')
      .select('template_id')
      .eq('user_id', user.id)
      .limit(1);
    if (error) throw error;
    updateCheck('assignments', { status: 'ok', detail: 'Table form_template_assignments accessible.' });
  } catch (err) {
    captureError(err, { check: 'assignments', context: 'AdminHealthCheck' });
    updateCheck('assignments', { status: 'error', detail: formatError(err) });
  }
}

export async function runClientsCheck(user: User, updateCheck: UpdateCheck) {
  updateCheck('clients', { status: 'running' });
  try {
    const { error } = await supabase
      .from('clients')
      .select('id')
      .eq('user_id', user.id)
      .limit(1);
    if (error) throw error;
    updateCheck('clients', { status: 'ok', detail: 'Table clients accessible et filtrée par utilisateur.' });
  } catch (err) {
    captureError(err, { check: 'clients', context: 'AdminHealthCheck' });
    updateCheck('clients', { status: 'error', detail: formatError(err) });
  }
}

export async function runDocumentsCheck(_user: User, updateCheck: UpdateCheck) {
  updateCheck('documents', { status: 'running' });
  try {
    const { error } = await supabase.from('documents').select('id').limit(1);
    if (error) throw error;
    updateCheck('documents', { status: 'ok', detail: 'Table documents accessible.' });
  } catch (err) {
    captureError(err, { check: 'documents', context: 'AdminHealthCheck' });
    updateCheck('documents', { status: 'error', detail: formatError(err) });
  }
}

export async function runSharedLinksCheck(user: User, updateCheck: UpdateCheck) {
  updateCheck('shared_links', { status: 'running' });
  try {
    const { error } = await supabase
      .from('shared_links')
      .select('id')
      .eq('user_id', user.id)
      .limit(1);
    if (error) throw error;
    updateCheck('shared_links', { status: 'ok', detail: 'Table shared_links accessible.' });
  } catch (err) {
    captureError(err, { check: 'shared_links', context: 'AdminHealthCheck' });
    updateCheck('shared_links', { status: 'error', detail: formatError(err) });
  }
}

export async function runRequestsCheck(user: User, updateCheck: UpdateCheck) {
  updateCheck('requests', { status: 'running' });
  try {
    const { error } = await supabase
      .from('requests')
      .select('id')
      .eq('user_id', user.id)
      .limit(1);
    if (error) throw error;
    updateCheck('requests', { status: 'ok', detail: 'Table requests accessible.' });
  } catch (err) {
    captureError(err, { check: 'requests', context: 'AdminHealthCheck' });
    updateCheck('requests', { status: 'error', detail: formatError(err) });
  }
}

export async function runFkIntegrityCheck(_user: User, updateCheck: UpdateCheck) {
  updateCheck('user_delete_fk_integrity', { status: 'running' });
  try {
    const { data, error } = await supabase.rpc('check_user_delete_fk_integrity');
    if (error) throw error;
    const offenders = (data ?? []) as FkOffender[];
    if (offenders.length === 0) {
      updateCheck('user_delete_fk_integrity', {
        status: 'ok',
        detail: 'Toutes les FK vers users utilisent ON DELETE CASCADE ou SET NULL.',
      });
    } else {
      const list = offenders
        .map((o) => `${o.table_name}.${o.constraint_name} (${o.on_delete})`)
        .join(' | ');
      updateCheck('user_delete_fk_integrity', {
        status: 'error',
        detail: `${offenders.length} contrainte(s) bloquante(s) : ${list}`,
      });
    }
  } catch (err) {
    captureError(err, { check: 'user_delete_fk_integrity', context: 'AdminHealthCheck' });
    updateCheck('user_delete_fk_integrity', { status: 'error', detail: formatError(err) });
  }
}

export async function runActivitySummaryCheck(_user: User, updateCheck: UpdateCheck) {
  updateCheck('vw_user_activity_summary', { status: 'running' });
  try {
    const { data, error } = await supabase.from('vw_user_activity_summary').select('id').limit(1);
    if (error) throw error;
    if ((data?.length ?? 0) === 0) {
      const { count: userCount } = await supabase
        .from('users')
        .select('id', { count: 'exact', head: true })
        .eq('is_admin', false)
        .is('deleted_at', null);
      if ((userCount ?? 0) > 0) {
        updateCheck('vw_user_activity_summary', {
          status: 'warning',
          detail: `La vue est accessible mais retourne 0 ligne alors que ${userCount} utilisateur(s) non-admin existent. Vérifier les policies RLS admin sur users, user_activity_events, requests, clients.`,
        });
      } else {
        updateCheck('vw_user_activity_summary', {
          status: 'ok',
          detail: 'Vue accessible. Aucun utilisateur non-admin en base (0 ligne attendu).',
        });
      }
    } else {
      updateCheck('vw_user_activity_summary', {
        status: 'ok',
        detail: "Vue security_invoker lisible par l'admin. Policies RLS sous-jacentes opérationnelles.",
      });
    }
  } catch (err) {
    captureError(err, { check: 'vw_user_activity_summary', context: 'AdminHealthCheck' });
    updateCheck('vw_user_activity_summary', { status: 'error', detail: formatError(err) });
  }
}

export async function runActivityEventsCheck(_user: User, updateCheck: UpdateCheck) {
  updateCheck('vw_admin_activity_events', { status: 'running' });
  try {
    const { data, error } = await supabase.from('vw_admin_activity_events').select('id').limit(1);
    if (error) throw error;
    if ((data?.length ?? 0) === 0) {
      const { count: eventCount } = await supabase
        .from('user_activity_events')
        .select('id', { count: 'exact', head: true })
        .limit(1);
      if ((eventCount ?? 0) > 0) {
        updateCheck('vw_admin_activity_events', {
          status: 'warning',
          detail: 'La vue est accessible mais retourne 0 ligne alors que des événements existent. Vérifier les policies RLS admin sur user_activity_events et users.',
        });
      } else {
        updateCheck('vw_admin_activity_events', {
          status: 'ok',
          detail: 'Vue accessible. Aucun événement en base (0 ligne attendu).',
        });
      }
    } else {
      updateCheck('vw_admin_activity_events', {
        status: 'ok',
        detail: "Vue security_invoker lisible par l'admin. Policies RLS sur user_activity_events opérationnelles.",
      });
    }
  } catch (err) {
    captureError(err, { check: 'vw_admin_activity_events', context: 'AdminHealthCheck' });
    updateCheck('vw_admin_activity_events', { status: 'error', detail: formatError(err) });
  }
}

// ---------------------------------------------------------------------------
// Security audit runners (added 2026-05-11)
// ---------------------------------------------------------------------------

export async function runSecDefGrantsCheck(_user: User, updateCheck: UpdateCheck) {
  updateCheck('secdef_public_grants', { status: 'running' });
  try {
    const { data, error } = await supabase.rpc('audit_secdef_public_grants');
    if (error) throw error;
    const offenders = (data ?? []) as SecDefOffender[];
    if (offenders.length === 0) {
      updateCheck('secdef_public_grants', {
        status: 'ok',
        detail: 'Aucune fonction SECURITY DEFINER n\'expose EXECUTE à anon ou PUBLIC.',
      });
    } else {
      const list = offenders
        .map((o) => `${o.function_name}(${o.args}) → [${(o.exposed_roles ?? []).join(', ')}]`)
        .join(' | ');
      updateCheck('secdef_public_grants', {
        status: 'error',
        detail: `${offenders.length} fonction(s) exposée(s) : ${list}`,
      });
    }
  } catch (err) {
    captureError(err, { check: 'secdef_public_grants', context: 'AdminHealthCheck' });
    updateCheck('secdef_public_grants', { status: 'error', detail: formatError(err) });
  }
}

export async function runFunctionSearchPathCheck(_user: User, updateCheck: UpdateCheck) {
  updateCheck('function_search_path', { status: 'running' });
  try {
    const { data, error } = await supabase.rpc('audit_function_search_path');
    if (error) throw error;
    const offenders = (data ?? []) as SearchPathOffender[];
    if (offenders.length === 0) {
      updateCheck('function_search_path', {
        status: 'ok',
        detail: 'Toutes les fonctions PL/pgSQL du schéma public ont un search_path verrouillé.',
      });
    } else {
      const list = offenders
        .map((o) => `${o.function_name}(${o.args})${o.security_definer ? ' [SECURITY DEFINER]' : ''}`)
        .join(' | ');
      updateCheck('function_search_path', {
        status: 'error',
        detail: `${offenders.length} fonction(s) sans search_path fixé : ${list}`,
      });
    }
  } catch (err) {
    captureError(err, { check: 'function_search_path', context: 'AdminHealthCheck' });
    updateCheck('function_search_path', { status: 'error', detail: formatError(err) });
  }
}

export async function runStoragePoliciesCheck(_user: User, updateCheck: UpdateCheck) {
  updateCheck('storage_broad_select_policies', { status: 'running' });
  try {
    const { data, error } = await supabase.rpc('audit_storage_public_select_policies');
    if (error) throw error;
    const offenders = (data ?? []) as StoragePolicyOffender[];
    if (offenders.length === 0) {
      updateCheck('storage_broad_select_policies', {
        status: 'ok',
        detail: 'Aucune policy SELECT publique sur storage.objects. Les buckets publics servent les URLs directement.',
      });
    } else {
      const list = offenders
        .map((o) => `"${o.policy_name}" (${o.using_expr})`)
        .join(' | ');
      updateCheck('storage_broad_select_policies', {
        status: 'error',
        detail: `${offenders.length} policy SELECT publique(s) détectée(s) : ${list}`,
      });
    }
  } catch (err) {
    captureError(err, { check: 'storage_broad_select_policies', context: 'AdminHealthCheck' });
    updateCheck('storage_broad_select_policies', { status: 'error', detail: formatError(err) });
  }
}

export async function runSecDefAuthenticatedGrantsCheck(_user: User, updateCheck: UpdateCheck) {
  updateCheck('secdef_authenticated_grants', { status: 'running' });
  try {
    const { data, error } = await supabase.rpc('audit_secdef_authenticated_grants');
    if (error) throw error;
    const offenders = (data ?? []) as SecDefAuthOffender[];
    if (offenders.length === 0) {
      updateCheck('secdef_authenticated_grants', {
        status: 'ok',
        detail: 'Aucune fonction SECURITY DEFINER hors allowlist n\'est exposée à authenticated.',
      });
    } else {
      const list = offenders
        .map((o) => `${o.function_name}(${o.args}) → [${(o.exposed_roles ?? []).join(', ')}]`)
        .join(' | ');
      updateCheck('secdef_authenticated_grants', {
        status: 'error',
        detail: `${offenders.length} fonction(s) non approuvée(s) exposée(s) à authenticated : ${list}`,
      });
    }
  } catch (err) {
    captureError(err, { check: 'secdef_authenticated_grants', context: 'AdminHealthCheck' });
    updateCheck('secdef_authenticated_grants', { status: 'error', detail: formatError(err) });
  }
}

export async function runRlsTablesWithoutPoliciesCheck(_user: User, updateCheck: UpdateCheck) {
  updateCheck('rls_tables_without_policies', { status: 'running' });
  try {
    const { data, error } = await supabase.rpc('audit_rls_tables_without_policies');
    if (error) throw error;
    const offenders = (data ?? []) as RlsNoPolicyOffender[];
    if (offenders.length === 0) {
      updateCheck('rls_tables_without_policies', {
        status: 'ok',
        detail: 'Toutes les tables RLS sans policy sont dans l\'allowlist approuvée (digest_deliveries, notification_tokens).',
      });
    } else {
      const list = offenders
        .map((o) => `${o.table_name}${o.has_intentional_comment ? ' [INTENTIONAL: présent]' : ' [pas de INTENTIONAL:]'}`)
        .join(' | ');
      updateCheck('rls_tables_without_policies', {
        status: 'error',
        detail: `${offenders.length} table(s) avec RLS activé, aucune policy, hors allowlist : ${list}`,
      });
    }
  } catch (err) {
    captureError(err, { check: 'rls_tables_without_policies', context: 'AdminHealthCheck' });
    updateCheck('rls_tables_without_policies', { status: 'error', detail: formatError(err) });
  }
}

export async function runSecDefJustificationCheck(_user: User, updateCheck: UpdateCheck) {
  updateCheck('secdef_exception_justifications', { status: 'running' });
  try {
    const { data, error } = await supabase.rpc('audit_secdef_missing_justification');
    if (error) throw error;
    const offenders = (data ?? []) as MissingJustificationOffender[];
    if (offenders.length === 0) {
      updateCheck('secdef_exception_justifications', {
        status: 'ok',
        detail: 'Toutes les exceptions SECURITY DEFINER approuvées ont un commentaire INTENTIONAL: en base.',
      });
    } else {
      const list = offenders
        .map((o) => o.status === 'MISSING'
          ? `${o.function_name}(${o.args}) [INTROUVABLE]`
          : `${o.function_name}(${o.args})`)
        .join(' | ');
      updateCheck('secdef_exception_justifications', {
        status: 'error',
        detail: `${offenders.length} fonction(s) sans justification documentée : ${list}`,
      });
    }
  } catch (err) {
    captureError(err, { check: 'secdef_exception_justifications', context: 'AdminHealthCheck' });
    updateCheck('secdef_exception_justifications', { status: 'error', detail: formatError(err) });
  }
}
